import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Icon } from '@lomarengas/design-system';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { LOADING_STATUS } from '../../common/constants';
import { ActionButtons, Loader, SectionHeader } from '../../common/components';
import {
  AccommodationGroupDetailsTranslations,
  getAccommodationGroupDetailsAsync,
  updateAccommodationGroupAsync,
  selectAccommodationGroupDetails,
  selectAccommodationGroupDetailsLoadingStatus,
  AccommodationGroupDetailsData,
} from './accommodationGroupsSlice';
import {
  AccountingsQueryFilters,
  getAllPurchaselAccountingGroupsAsync,
  getAllSellAccountingGroupsAsync,
  getAllSellVatsAsync,
  selectPurchaseAccountingGroups,
  selectSellAccountingGroups,
  selectSellVats,
  SellVatDetailsData,
} from '../accountings/accountingSlice';

function AccommodationGroupDetails() {
  const backLinkUrl = '/accommodations/accommodationgroups';
  const params = useParams();
  const [submitted, setSubmitted] = useState(false);
  const dispatch = useAppDispatch();

  const initialAccountingFilter: AccountingsQueryFilters = {
    pageNumber: 1,
    pageSize: 100,
    culture: 'fi_FI',
  };
  const navigate = useNavigate();
  const [accountingFilter, setAccountingFilter] = useState(
    initialAccountingFilter,
  );

  const accommodationGroupDetails = useAppSelector(
    selectAccommodationGroupDetails,
  );
  const purchaseAccountingGroups = useAppSelector(
    selectPurchaseAccountingGroups,
  );
  const sellAccountingGroups = useAppSelector(selectSellAccountingGroups);
  const [accommodationGroupTemp, setAccommodationGroup] = useState(
    accommodationGroupDetails,
  );
  const accommodationGroupDetailsLoadingStatus = useAppSelector(
    selectAccommodationGroupDetailsLoadingStatus,
  );

  const handleChangeSellAccountingGroup = event => {
    if (accommodationGroupTemp != null) {
      const storedSellVatData: AccommodationGroupDetailsData = {
        ...accommodationGroupTemp,
        sellAccountingGroupId: event.value,
      };
      setAccommodationGroup(storedSellVatData);
    }
  };

  const handleChangePurchaseAccountingGroup = event => {
    if (accommodationGroupTemp != null) {
      const storedSellVatData: AccommodationGroupDetailsData = {
        ...accommodationGroupTemp,
        purchaseAccountingGroupId: event.value,
      };
      setAccommodationGroup(storedSellVatData);
    }
  };
  function getItem(locale, keyId, item) {
    if (accommodationGroupDetailsLoadingStatus === LOADING_STATUS.IDLE) {
      const found = item.translations.filter(
        a => a.locale === locale && a.keyId === keyId,
      );
      if (found && found[0] && found[0].text) {
        return found[0].text;
      }
    }
    return '';
  }

  const handleInputChange = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    setAccommodationGroup({ ...accommodationGroupTemp, [name]: value });
    setSubmitted(false);
  };

  const sellAccountingGroupList = sellAccountingGroups.map(svat => {
    return { value: svat.id, label: svat.name };
  });

  const purchaseAccountingGroupList = purchaseAccountingGroups.map(svat => {
    return { value: svat.id, label: svat.name };
  });
  const editTranslations = (translations, keyId, locale, value) => {
    let exist = false;

    translations = translations.map(item => {
      const temp = { ...item, item };
      if (temp.keyId === keyId && temp.locale === locale) {
        temp.text = value;
        exist = true;
      }
      return temp;
    });
    if (!exist) {
      const newitem: AccommodationGroupDetailsTranslations = {
        locale,
        keyId,
        text: value,
      };
      translations = [...translations, newitem];
      return translations;
    }
    return translations;
  };

  const handleInputChangeTranslation = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { id } = target;
    const { name } = target;
    let translationsTemp = accommodationGroupTemp.translations;
    if (id.endsWith('FiFi')) {
      translationsTemp = editTranslations(
        translationsTemp,
        name,
        'fi_FI',
        value,
      );
    } else if (id.endsWith('EnEn')) {
      translationsTemp = editTranslations(
        translationsTemp,
        name,
        'en_EN',
        value,
      );
    } else if (id.endsWith('RuRu')) {
      translationsTemp = editTranslations(
        translationsTemp,
        name,
        'ru_RU',
        value,
      );
    }

    const storeAccommodationGroup = { ...accommodationGroupTemp };
    storeAccommodationGroup.translations = translationsTemp;
    setAccommodationGroup(storeAccommodationGroup);
    setSubmitted(false);
  };

  function dbDatetoCalendar(date) {
    // this is rude temp solution until we have proper
    // date dandling and calendar options
    const tempDateObj = new Date(date);
    let retval = tempDateObj.getFullYear().toString();
    retval += '-';
    let temMonth = '0';
    temMonth += tempDateObj.getMonth() + 1;
    temMonth = temMonth.slice(-2);
    retval += temMonth;
    retval += '-';
    let tempDate = '0';
    tempDate += tempDateObj.getDate();
    tempDate = tempDate.slice(-2);
    retval += tempDate;

    return retval;
  }
  const updateAccommodationGroup = () => {
    dispatch(updateAccommodationGroupAsync(accommodationGroupTemp));
    navigate(`/accommodations/accommodationgroups`);
  };
  const initFetch = useCallback(() => {
    setAccommodationGroup({
      id: '',
      groupLetter: '',
      hideFromLomarengasSite: false,
      sellAccountingGroupId: '',
      purchaseAccountingGroupId: '',
      bypassPurchaseVatDeduction: false,
      translations: [],
      isDeleted: false,
    } as AccommodationGroupDetailsData);
    dispatch(getAccommodationGroupDetailsAsync(params.accommodationgroupId));
    dispatch(getAllSellAccountingGroupsAsync(accountingFilter));
    dispatch(getAllPurchaselAccountingGroupsAsync(accountingFilter));
  }, [dispatch, params.Id]);

  useEffect(() => {
    initFetch();
  }, [initFetch]);

  useEffect(() => {}, [accommodationGroupTemp]);

  useEffect(() => {
    if (accommodationGroupDetails != null) {
      setAccommodationGroup(accommodationGroupDetails);
    }
  }, [accommodationGroupDetails]);

  return (
    <>
      <Helmet>
        <title>Majoitusryhmä - Tiedot</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={['Majoitukset ja Ryhmät', 'Majoitusryhmät']}
          Title={getItem(
            'fi_FI',
            'AccommodationGroupName',
            accommodationGroupTemp,
          )}
        />

        <Loader
          IsLoading={
            accommodationGroupDetailsLoadingStatus === LOADING_STATUS.LOADING
          }
        />

        {accommodationGroupDetailsLoadingStatus === LOADING_STATUS.IDLE && (
          <Container className="container-form mt-48">
            <Form.Group className="mb-40">
              <Form.Label
                className="required"
                htmlFor="AccommodationGroupNameFiFi"
              >
                Ryhmän Nimi FI
              </Form.Label>
              <Form.Control
                autoComplete="off"
                id="AccommodationGroupNameFiFi"
                required
                value={getItem(
                  'fi_FI',
                  'AccommodationGroupName',
                  accommodationGroupTemp,
                )}
                onChange={handleInputChangeTranslation}
                name="AccommodationGroupName"
              />
            </Form.Group>

            <Form.Group className="mb-40">
              <Form.Label
                className="required"
                htmlFor="AccommodationGroupNameEnEn"
              >
                Ryhmän Nimi EN
              </Form.Label>
              <Form.Control
                autoComplete="off"
                id="AccommodationGroupNameEnEn"
                required
                value={getItem(
                  'en_EN',
                  'AccommodationGroupName',
                  accommodationGroupTemp,
                )}
                onChange={handleInputChangeTranslation}
                name="AccommodationGroupName"
              />
            </Form.Group>

            {/* <Form.Group className="mb-40"> */}
            {/*  <Form.Label */}
            {/*    className="required" */}
            {/*    htmlFor="AccommodationGroupNameRuRu" */}
            {/*  > */}
            {/*    Ryhmän Nimi RU */}
            {/*  </Form.Label> */}
            {/*  <Form.Control */}
            {/*    autoComplete="off" */}
            {/*    id="AccommodationGroupNameRuRu" */}
            {/*    required */}
            {/*    value={getItem( */}
            {/*      'ru_RU', */}
            {/*      'AccommodationGroupName', */}
            {/*      accommodationGroupTemp, */}
            {/*    )} */}
            {/*    onChange={handleInputChangeTranslation} */}
            {/*    name="AccommodationGroupName" */}
            {/*  /> */}
            {/* </Form.Group> */}

            <Form.Group className="mb-40">
              <Form.Label className="required" htmlFor="groupLetter">
                Ryhmätunnus
              </Form.Label>
              <Form.Control
                autoComplete="off"
                id="groupLetter"
                required
                value={accommodationGroupTemp.groupLetter || ''}
                onChange={handleInputChange}
                name="groupLetter"
              />
            </Form.Group>

            <Form.Group className="mb-40">
              <Form.Check
                id="hideFromLomarengasSite"
                type="checkbox"
                label="Piilota Lomarenkaan verkkokaupasta"
                name="hideFromLomarengasSite"
                onChange={handleInputChange}
                checked={accommodationGroupTemp.hideFromLomarengasSite}
              />
            </Form.Group>

            <Form.Group className="mb-40">
              <Form.Check
                id="bypassPurchaseVatDeduction"
                type="checkbox"
                label="Ohita arvonlisäveron vähennys ostohinnasta"
                name="bypassPurchaseVatDeduction"
                onChange={handleInputChange}
                checked={accommodationGroupTemp.bypassPurchaseVatDeduction}
              />
            </Form.Group>

            <Form.Group className="mb-40">
              <Row xs={1} xl={1} className="mb-40">
                <Col>
                  <hr className="mt-16 mb-32" />
                  <h3 className="h6 mb-16">Myynti kirjanpitotilit</h3>
                  <Select
                    inputId="sellAccountingGroupId"
                    name="sellAccountingGroupId"
                    placeholder="Valitse Kirjanpitoryhmä"
                    isClearable
                    isSearchable
                    options={sellAccountingGroupList}
                    onChange={option => handleChangeSellAccountingGroup(option)}
                    value={sellAccountingGroupList.filter(
                      option =>
                        option.value ===
                        accommodationGroupTemp.sellAccountingGroupId,
                    )}
                  />
                </Col>
                <Col>
                  <Link
                    className="d-block mt-24"
                    to={`/accounting/sellaccountinggroups/${accommodationGroupTemp.sellAccountingGroupId}`}
                  >
                    Myynti Tilimääritykset
                    <Icon CssClass="ms-8" Name="box-arrow-right" />
                  </Link>
                </Col>
              </Row>
              <Row xs={1} xl={1} className="mb-40">
                <Col>
                  <hr className="mt-16 mb-32" />
                  <h3 className="h6 mb-16">Osto kirjanpitotilit</h3>
                  <Select
                    inputId="sellAccountingGroupId"
                    name="sellAccountingGroupId"
                    placeholder="Valitse Kirjanpitoryhmä"
                    isClearable
                    isSearchable
                    options={purchaseAccountingGroupList}
                    onChange={option =>
                      handleChangePurchaseAccountingGroup(option)
                    }
                    value={purchaseAccountingGroupList.filter(
                      option =>
                        option.value ===
                        accommodationGroupTemp.purchaseAccountingGroupId,
                    )}
                  />
                </Col>
                <Col>
                  <Link
                    className="d-block mt-24"
                    to={`/accounting/purchaseaccountinggroups/${accommodationGroupTemp.purchaseAccountingGroupId}`}
                  >
                    Osto Tilimääritykset
                    <Icon CssClass="ms-8" Name="box-arrow-right" />
                  </Link>
                </Col>
              </Row>
            </Form.Group>

            <ActionButtons
              CancelButtonUrl={backLinkUrl}
              HandleSave={updateAccommodationGroup}
            />
          </Container>
        )}
      </Container>
    </>
  );
}

export default AccommodationGroupDetails;
